<hlm-dialog [state]="state()" (closed)="onCancel()">
  <hlm-dialog-content
    *brnDialogContent="let ctx"
    class="w-[min(48rem,calc(100vw-2rem))] md:w-[min(48rem,calc(100vw-2rem))] max-w-none min-w-0 h-[750px] max-h-[90vh] flex flex-col"
  >
    <app-wizard-dialog-header
      [eyebrow]="config().eyebrow"
      [title]="config().title"
      [steps]="wizardSteps()"
      [showTimeline]="config().showTimeline !== false && totalSteps() > 1"
      (stepJump)="goToStep($event)"
    />

    <!-- Step Content -->
    <div hlmSeparator class="my-2"></div>
    <div class="flex-1 py-4 relative w-full min-h-0">
      <div
        class="h-full w-full transition-all duration-300 ease-in-out overflow-hidden"
      >
        <div
          class="h-full overflow-y-auto space-y-4 animate-in fade-in-50 duration-300 px-1"
        >
          <app-step-lead [text]="currentLead()" />

          <!-- ══════════════════════════════════════════════════════════════ -->
          <!-- STEP 1: Source Selection                                      -->
          <!-- ══════════════════════════════════════════════════════════════ -->
@if (currentStep() === 1) {
            <div class="overflow-hidden rounded-md border border-border">
              @for (source of addressSources; track source.key) {
                <button
                  type="button"
                  class="flex w-full items-start gap-3 border-b border-border/60 px-4 py-3.5 text-left transition-colors last:border-b-0"
                  [class.bg-muted]="addressSource() === source.key"
                  [class.hover:bg-muted]="addressSource() !== source.key"
                  (click)="selectSource(source.key)"
                >
                  <ng-icon
                    [name]="source.icon"
                    class="mt-0.5 h-4 w-4 shrink-0"
                    [class.text-primary]="addressSource() === source.key"
                    [class.text-muted-foreground]="addressSource() !== source.key"
                  />
                  <span class="min-w-0 flex-1">
                    <span
                      class="block text-sm text-foreground"
                      [class.font-bold]="addressSource() === source.key"
                      >{{ source.title }}</span
                    >
                    <span
                      class="mt-0.5 block max-w-[62ch] text-xs leading-normal text-muted-foreground text-pretty"
                      >{{ source.description }}</span
                    >
                  </span>
                  @if (addressSource() === source.key) {
                    <ng-icon name="lucideCheck" class="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  }
                </button>
              }
            </div>
          }

          <!-- ══════════════════════════════════════════════════════════════ -->
          <!-- STEP 2: Address Input                                         -->
          <!-- ══════════════════════════════════════════════════════════════ -->
          @if (currentStep() === 2) {
            <!-- ─── PLZ Mode ──────────────────────────────────────────── -->
            @if (addressSource() === "plz") {
              <div class="flex flex-col gap-4">
                <app-field-shell
                  label="Postleitzahlen"
                  required
                  helpText="Eine oder mehrere 5-stellige PLZ, kommagetrennt möglich. Wir ermitteln alle verfügbaren Adressen in diesen Gebieten."
                  [errorText]="plzError()"
                >
                  <div class="flex items-center gap-2.5">
                    <input
                      hlmInput
                      type="text"
                      class="flex-1 font-mono"
                      placeholder="63303, 60311, 65183"
                      [ngModel]="plzInput()"
                      (ngModelChange)="plzInput.set($event)"
                      (keydown)="onPlzKeydown($event)"
                    />
                    <button
                      hlmBtn
                      variant="outline"
                      type="button"
                      class="shrink-0"
                      (click)="addPlz()"
                      [disabled]="!plzInput().trim()"
                    >
                      <ng-icon name="lucidePlus" class="w-4 h-4 mr-1"></ng-icon>
                      Hinzufügen
                    </button>
                  </div>
                </app-field-shell>

                @if (plzList().length > 0) {
                  <div class="flex flex-wrap gap-2">
                    @for (plz of plzList(); track plz) {
                      <span class="ks-chip font-mono">
                        {{ plz }}
                        <button
                          type="button"
                          class="opacity-60 transition-opacity hover:opacity-100"
                          [attr.aria-label]="plz + ' entfernen'"
                          (click)="removePlz(plz)"
                        >
                          <ng-icon name="lucideX" class="h-3 w-3"></ng-icon>
                        </button>
                      </span>
                    }
                  </div>
                }
              </div>
            }

            <!-- ─── Area Mode ─────────────────────────────────────────── -->
            @if (addressSource() === "area") {
              <div class="flex flex-col gap-3">
                <p class="text-sm text-muted-foreground">
                  Suchen Sie den Ort und klicken Sie die Ecken des Gebiets in die Karte. Sie können mehrere Flächen einzeichnen und jede nachträglich anpassen.
                </p>
                <app-campaign-area-picker [organizationId]="organizationId()" [(polygons)]="areaPolygons" />
              </div>
            }

            <!-- ─── CSV Mode ──────────────────────────────────────────── -->
            @if (addressSource() === "csv") {

              <p class="text-sm text-muted-foreground mb-4">
                Laden Sie eine CSV-Datei mit Ihren Adressen hoch. Verwenden Sie
                die Vorlage als Ausgangspunkt — die Validierung erfolgt
                automatisch im nächsten Schritt.
              </p>

              <!-- Template download -->
              <div class="flex items-center gap-2 mb-4">
                <button
                  hlmBtn
                  variant="outline"
                  size="sm"
                  type="button"
                  (click)="downloadTemplate()"
                  class="flex items-center gap-2"
                >
                  <ng-icon name="lucideDownload" class="w-3.5 h-3.5"></ng-icon>
                  CSV-Vorlage herunterladen
                </button>
                <span class="text-xs text-muted-foreground">
                  Pflichtfelder: street, house_number, postal_code, city
                </span>
              </div>

              @if (!selectedFile()) {
                <!-- Drop zone -->
                <div
                  class="border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer"
                  title="CSV-Datei auswählen, um fortzufahren"
                  [class.border-primary]="isDragOver()"
                  [ngClass]="isDragOver() ? 'bg-primary/5' : 'hover:bg-muted/30'"
                  [class.border-border]="!isDragOver()"
                  (dragover)="onDragOver($event)"
                  (dragleave)="onDragLeave($event)"
                  (drop)="onDrop($event)"
                  (click)="fileInput.click()"
                >
                  <input
                    #fileInput
                    type="file"
                    accept=".csv"
                    class="hidden"
                    (change)="onFileSelected($event)"
                  />
                  <ng-icon
                    name="lucideUpload"
                    class="w-10 h-10 text-muted-foreground mx-auto mb-3"
                  ></ng-icon>
                  <p class="font-normal">CSV-Datei hierher ziehen</p>
                  <p class="text-sm text-muted-foreground mt-1">
                    oder klicken zum Auswählen
                  </p>
                  <p class="text-xs text-muted-foreground mt-3">
                    .csv · UTF-8 oder UTF-8 BOM · max. 10 MB
                  </p>
                </div>
              } @else {
                <!-- Selected file -->
                <div
                  class="flex items-center justify-between p-4 rounded-xl bg-success-soft border border-success"
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="h-10 w-10 bg-success-soft rounded-lg flex items-center justify-center flex-shrink-0"
                    >
                      <ng-icon
                        name="lucideFileSpreadsheet"
                        class="w-4 h-4 text-primary"
                      ></ng-icon>
                    </div>
                    <div>
                      <p class="text-sm font-normal text-success">
                        {{ selectedFile()!.name }}
                      </p>
                      <p class="text-xs text-success">
                        {{ formatFileSize(selectedFile()!.size) }} · Bereit zur
                        Validierung
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="p-1.5 rounded-md hover:bg-destructive/10 text-success hover:text-destructive transition-colors"
                    (click)="clearFile()"
                    title="Datei entfernen"
                  >
                    <ng-icon name="lucideTrash" class="w-4 h-4"></ng-icon>
                  </button>
                </div>
                <p class="text-xs text-muted-foreground mt-2">
                  Klicken Sie auf „Weiter" — die Datei wird automatisch
                  verarbeitet und validiert.
                </p>
              }

              @if (uploadError()) {
                <div
                  class="mt-3 bg-destructive/10 border border-destructive/20 text-destructive px-3 py-2 rounded-lg text-sm"
                >
                  {{ uploadError() }}
                </div>
              }
            }

            <!-- ─── Existing Addresses Mode ─────────────────────────────── -->
@if (addressSource() === 'existing') {
              <app-field-shell
                label="Adressen"
                required
                [hint]="selectedExistingCount() + ' ausgewählt'"
              >
                <app-address-multi-select
                  [addresses]="filteredExistingAddresses()"
                  [selected]="selectedExistingAddresses()"
                  [isLoading]="isLoadingExistingAddresses()"
                  [error]="existingAddressesError()"
                  [totalCount]="existingAddresses().length"
                  (searchChanged)="addressSearchQuery.set($event)"
                  (toggled)="toggleExistingAddress($event.addressId)"
                />
              </app-field-shell>
            }
          }

          <!-- ══════════════════════════════════════════════════════════════ -->
          <!-- STEP 3: Address Validation                                    -->
          <!-- ══════════════════════════════════════════════════════════════ -->
          @if (currentStep() === 3) {

            <p class="text-sm text-muted-foreground mb-4">
              @if (addressSource() === 'csv') {
                Wir prüfen, ob die Adressen aus Ihrer Datei gefunden werden.
              } @else {
                Wir prüfen die Adressen und die verfügbare Gebietsabdeckung.
              }
            </p>

            <!-- ── PLZ: OSM loading spinner ─────────────────────────────── -->
            @if (isLoadingPlzAddresses()) {
              <div class="flex flex-col items-center justify-center py-8 text-center">
                <div class="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <p class="text-muted-foreground">Adressdaten werden von OpenStreetMap abgerufen…</p>
                <p class="text-xs text-muted-foreground mt-1 max-w-sm">
                  @if (addressSource() === 'area') {
                    Für {{ areaPolygons().length === 1 ? 'das eingezeichnete Gebiet' : 'die ' + areaPolygons().length + ' eingezeichneten Gebiete' }} ({{ areaKm2().toLocaleString('de-DE', { maximumFractionDigits: 2 }) }}&nbsp;km²)
                  } @else {
                    Für {{ plzList().length }}
                    {{ plzList().length === 1 ? 'Postleitzahl' : 'Postleitzahlen' }}
                  }
                  werden alle Gebäudeadressen gesammelt. Bei großen Gebieten kann
                  dies bis zu ~2&nbsp;Minuten dauern.
                </p>
                <p class="text-xs font-normal text-muted-foreground mt-3 tabular-nums">
                  Verstrichene Zeit: {{ loadElapsedLabel() }}
                </p>
              </div>
            }

            <!-- ── CSV: file processing spinner ────────────────────────── -->
            @if (isCsvUploading()) {
              <div class="flex flex-col items-center justify-center py-8">
                <div class="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <p class="text-muted-foreground">CSV wird verarbeitet...</p>
                <p class="text-xs text-muted-foreground mt-1">
                  {{ selectedFile()?.name }} wird eingelesen und geprüft.
                </p>
              </div>
            }

            <!-- ── SI validation spinner ────────────────────────────────── -->
            @if (!isLoadingPlzAddresses() && !isCsvUploading() && isValidatingAddresses()) {
              <div class="flex flex-col items-center justify-center py-8 text-center">
                <div class="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <p class="text-muted-foreground">Adressen werden geprüft …</p>
                <p class="text-xs text-muted-foreground mt-1">
                  {{ totalAddresses().toLocaleString("de-DE") }} Adressen werden geprüft.
                </p>
                <p class="text-xs font-normal text-muted-foreground mt-3 tabular-nums">
                  Verstrichene Zeit: {{ loadElapsedLabel() }}
                </p>
              </div>
            }

            <!-- ── CSV upload error ─────────────────────────────────────── -->
            @if (!isCsvUploading() && uploadError()) {
              <div class="p-4 rounded-lg border border-destructive/20 bg-destructive/5 mb-4">
                <div class="flex items-center gap-2 text-destructive font-normal mb-1">
                  <ng-icon name="lucideTriangleAlert" class="w-5 h-5"></ng-icon>
                  CSV-Verarbeitung fehlgeschlagen
                </div>
                <p class="text-sm text-destructive/80">{{ uploadError() }}</p>
                <button hlmBtn variant="outline" size="sm" type="button" (click)="retryValidation()" class="mt-3">
                  Erneut versuchen
                </button>
              </div>
            }

            @if (addressSource() === 'area' && areaSkipped() > 0 && !isLoadingPlzAddresses() && !plzFetchError()) {
              <p class="mb-4 text-xs text-muted-foreground" role="note">
                {{ areaSkipped().toLocaleString('de-DE') }} Gebäude im Gebiet haben in OpenStreetMap keine vollständige Adresse (Straße oder PLZ fehlt) und werden nicht übernommen.
              </p>
            }

            <!-- ── PLZ fetch failed / no addresses (OSM timeout) ─────────── -->
            @if (!isLoadingPlzAddresses() && !isValidatingAddresses() && plzFetchError()) {
              <div class="p-4 rounded-lg border border-warning bg-warning-soft mb-4">
                <div class="flex items-center gap-2 text-warning font-normal mb-1">
                  <ng-icon name="lucideTriangleAlert" class="w-5 h-5"></ng-icon>
                  Adressen konnten nicht geladen werden
                </div>
                <p class="text-sm text-warning">{{ plzFetchError() }}</p>
                <button hlmBtn variant="outline" size="sm" type="button" (click)="retryValidation()" class="mt-3">
                  Erneut versuchen
                </button>
              </div>
            }

            <!-- ── CSV results (shown as soon as CSV is done, even while SI runs) -->
            @if (!isCsvUploading() && addressSource() === 'csv' && csvValidation()) {
              <!-- CSV parse summary (compact) -->
              <p class="text-sm text-muted-foreground mb-3">
                <span class="font-normal text-foreground">
                  {{ csvValidation()!.validAddresses.toLocaleString("de-DE") }} Zeilen eingelesen
                </span>
                @if (csvValidation()!.invalidAddresses > 0) {
                  <span class="text-destructive"> · {{ csvValidation()!.invalidAddresses }} übersprungen</span>
                }
                @if (csvValidation()!.duplicatesRemoved > 0) {
                  <span class="text-warning"> · {{ csvValidation()!.duplicatesRemoved }} Duplikate entfernt</span>
                }
              </p>

              <!-- CSV hard errors (rows skipped) -->
              @if (csvValidation()!.errors.length > 0) {
                <div class="border rounded-lg overflow-hidden mb-3">
                  <div class="bg-destructive/5 px-3 py-2 border-b">
                    <p class="text-sm font-normal text-destructive flex items-center gap-2">
                      <ng-icon name="lucideTriangleAlert" class="w-4 h-4"></ng-icon>
                      {{ csvValidation()!.errors.length }}
                      {{ csvValidation()!.errors.length === 1 ? 'Zeile übersprungen' : 'Zeilen übersprungen' }}
                    </p>
                  </div>
                  <div class="max-h-32 overflow-y-auto divide-y">
                    @for (err of csvValidation()!.errors.slice(0, 8); track $index) {
                      <div class="px-3 py-1.5 text-sm flex gap-2">
                        <span class="text-muted-foreground flex-shrink-0">
                          @if (err.row > 0) { Zeile {{ err.row }}: } @else { Datei: }
                        </span>
                        <span>{{ err.reason }}</span>
                      </div>
                    }
                    @if (csvValidation()!.errors.length > 8) {
                      <div class="px-3 py-1.5 text-xs text-muted-foreground italic">
                        ... und {{ csvValidation()!.errors.length - 8 }} weitere
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- CSV soft warnings (rows still included) -->
              @if (csvValidation()!.warnings.length > 0) {
                <div class="border rounded-lg overflow-hidden mb-3">
                  <div class="bg-warning-soft px-3 py-2 border-b border-warning">
                    <p class="text-sm font-normal text-warning flex items-center gap-2">
                      <ng-icon name="lucideInfo" class="w-4 h-4"></ng-icon>
                      {{ csvValidation()!.warnings.length }}
                      {{ csvValidation()!.warnings.length === 1 ? 'Hinweis' : 'Hinweise' }}
                      — Zeilen sind enthalten, Analyse eingeschränkt
                    </p>
                  </div>
                  <div class="max-h-32 overflow-y-auto divide-y">
                    @for (w of csvValidation()!.warnings.slice(0, 8); track $index) {
                      <div class="px-3 py-1.5 text-sm flex gap-2">
                        <span class="text-warning flex-shrink-0">Zeile {{ w.row }}:</span>
                        <span class="text-warning">{{ w.reason }}</span>
                      </div>
                    }
                    @if (csvValidation()!.warnings.length > 8) {
                      <div class="px-3 py-1.5 text-xs text-muted-foreground italic">
                        ... und {{ csvValidation()!.warnings.length - 8 }} weitere
                      </div>
                    }
                  </div>
                </div>
              }

              @if (csvValidation()!.validAddresses === 0) {
                <div class="p-4 rounded-lg border border-destructive/20 bg-destructive/5 mb-4">
                  <div class="flex items-center gap-2 text-destructive font-normal text-sm">
                    <ng-icon name="lucideTriangleAlert" class="w-4 h-4"></ng-icon>
                    Keine gültigen Adressen in der Datei.
                  </div>
                  <p class="text-xs text-destructive/70 mt-1">
                    Bitte gehen Sie zurück, korrigieren Sie die CSV-Datei und versuchen Sie es erneut.
                  </p>
                </div>
              }
            }

            <!-- ── SI validation error ──────────────────────────────────── -->
            @if (!isValidatingAddresses() && siValidationError()) {
              <div class="p-4 rounded-lg border border-warning bg-warning-soft mb-4">
                <div class="flex items-center gap-2 text-warning font-normal mb-1">
                  <ng-icon name="lucideTriangleAlert" class="w-5 h-5"></ng-icon>
                  Vorab-Validierung nicht verfügbar
                </div>
                <p class="text-sm text-warning">{{ siValidationError() }}</p>
                @if (totalAddresses() > 0) {
                  <p class="text-xs text-warning mt-1">
                    Sie können trotzdem fortfahren — die Validierung erfolgt beim Erstellen der Kampagne.
                  </p>
                }
                <button hlmBtn variant="outline" size="sm" type="button" (click)="retryValidation()" class="mt-3">
                  Erneut versuchen
                </button>
              </div>
            }

            <!-- ── SI validation results ────────────────────────────────── -->
            @if (!isValidatingAddresses() && siValidation()) {
              <!-- Validation stats — hairline-joined tiles, per the design -->
              <div
                class="mb-4 grid grid-cols-3 gap-px overflow-hidden rounded-md border border-border bg-border"
              >
                <div class="bg-success-soft p-4">
                  <div class="flex items-center gap-1.5">
                    <span class="h-[5px] w-[5px] rounded-full bg-success"></span>
                    <span
                      class="text-2xs font-bold uppercase tracking-wider text-success"
                      >Bereit</span
                    >
                  </div>
                  <div class="mt-2 font-mono text-2xl text-success">
                    {{ (siValidation()!.validCount | number) ?? '0' }}
                  </div>
                </div>

                <div class="p-4" [class]="totalFailedCount() > 0 ? 'bg-danger-soft' : 'bg-background'">
                  <div class="flex items-center gap-1.5">
                    <span
                      class="h-[5px] w-[5px] rounded-full"
                      [class]="totalFailedCount() > 0 ? 'bg-destructive' : 'bg-muted-foreground/40'"
                    ></span>
                    <span
                      class="text-2xs font-bold uppercase tracking-wider"
                      [class]="totalFailedCount() > 0 ? 'text-destructive' : 'text-muted-foreground'"
                      >Nicht geocodierbar</span
                    >
                  </div>
                  <div
                    class="mt-2 font-mono text-2xl"
                    [class]="totalFailedCount() > 0 ? 'text-destructive' : 'text-muted-foreground'"
                  >
                    {{ (totalFailedCount() | number) ?? '0' }}
                  </div>
                </div>

                <div class="p-4" [class]="siValidation()!.areaOk ? 'bg-success-soft' : 'bg-danger-soft'">
                  <div class="flex items-center gap-1.5">
                    <span
                      class="h-[5px] w-[5px] rounded-full"
                      [class]="siValidation()!.areaOk ? 'bg-success' : 'bg-destructive'"
                    ></span>
                    <span
                      class="text-2xs font-bold uppercase tracking-wider"
                      [class]="siValidation()!.areaOk ? 'text-success' : 'text-destructive'"
                      >Gebiet</span
                    >
                  </div>
                  <div
                    class="mt-2 font-mono text-2xl"
                    [class]="siValidation()!.areaOk ? 'text-success' : 'text-destructive'"
                  >
                    {{ siValidation()!.areaOk ? 'OK' : 'Zu groß' }}
                  </div>
                </div>

                @if (siValidation()!.areaSqm) {
                  <div
                    class="col-span-3 flex items-baseline justify-between bg-background px-4 py-2.5"
                  >
                    <span class="text-xs text-muted-foreground">Abgedeckte Fläche</span>
                    <span class="font-mono text-xs text-foreground"
                      >ca. {{ (siValidation()!.areaSqm! / 1000000).toFixed(1) }} km²</span
                    >
                  </div>
                }
              </div>

              @if (!siValidation()!.areaOk) {
                <div class="p-3 rounded-lg border border-destructive/20 bg-destructive/5 mb-3">
                  <div class="flex items-center gap-2 text-destructive font-normal text-sm mb-1">
                    <ng-icon name="lucideTriangleAlert" class="w-4 h-4"></ng-icon>
                    Gebiet zu groß — Adressen liegen zu weit auseinander.
                  </div>
                  @for (rf of siValidation()!.requestFailures; track $index) {
                    <p class="text-xs text-destructive/80 mt-1">{{ rf.detail }}</p>
                  }
                  @if (areaOutliers().length > 0) {
                    <p class="text-xs text-destructive/60 mt-2 mb-1.5">Entfernen Sie Ausreißer, um das Gebiet zu verkleinern:</p>
                    <div class="space-y-1">
                      @for (outlier of areaOutliers(); track outlier.addressId) {
                        <div class="flex items-center justify-between gap-2 rounded px-2 py-1 bg-white/40">
                          <span class="text-xs text-destructive/80 flex-1 min-w-0 truncate">
                            {{ outlier.street }} {{ outlier.houseNumber }}, {{ outlier.postalCode }} {{ outlier.city }}
                            <span class="text-destructive/50 ml-1">({{ outlier.distKm.toFixed(0) }} km vom Zentrum)</span>
                          </span>
                          <button
                            type="button"
                            class="flex-shrink-0 text-xs text-destructive underline hover:no-underline transition-all disabled:opacity-40"
                            [disabled]="isValidatingAddresses()"
                            (click)="removeOutlierAndRevalidate(outlier.addressId)"
                          >
                            Entfernen
                          </button>
                        </div>
                      }
                    </div>
                  } @else {
                    <p class="text-xs text-destructive/70 mt-2">
                      Bitte gehen Sie zurück und wählen Sie weniger oder räumlich näher beieinanderliegende Adressen.
                    </p>
                  }
                </div>
              }

              @if (allFailures().length > 0) {
                <div class="mb-3">
                  <app-summary-section
                    [title]="allFailures().length + ' Adressen nicht geocodierbar'"
                    [editable]="false"
                  >
                    @for (f of allFailures().slice(0, 10); track $index) {
                      <app-summary-row [label]="f.label" [value]="f.reason" monoLabel />
                    }
                    @if (allFailures().length > 10) {
                      <app-summary-row
                        label=""
                        [value]="'… und ' + (allFailures().length - 10) + ' weitere'"
                        tone="muted"
                      />
                    }
                  </app-summary-section>
                </div>
              }

              @if (siValidation()!.areaOk && effectiveAddresses() > 0) {
                <p class="max-w-[62ch] text-xs leading-normal text-muted-foreground text-pretty">
                  {{ (effectiveAddresses() | number) ?? '0' }} Adressen sind bereit für die Kampagne.
                </p>
                @if (packageSelectionError()) {
                  <p class="mt-2 text-sm text-destructive" role="alert">{{ packageSelectionError() }}</p>
                }
              }
            }

          }

          <!-- ══════════════════════════════════════════════════════════════ -->
          <!-- STEP 4: Package (auto-selected, read-only)                    -->
          <!-- ══════════════════════════════════════════════════════════════ -->
          @if (currentStep() === 4) {
            <div class="flex flex-col gap-4">
              <p class="max-w-[62ch] text-xs leading-normal text-muted-foreground text-pretty">
                Basierend auf {{ effectiveAddresses() | number }} validierten Adressen wurde
                automatisch das passende Paket ausgewählt.
              </p>

              @if (packageSelectionError()) {
                <div class="rounded-md border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive" role="alert">
                  {{ packageSelectionError() }}
                </div>
              }

              @if (packageMeta()) {
              <div class="rounded-md border border-border p-5">
                <div class="flex items-start gap-4">
                  <div class="min-w-0 flex-1">
                    <div class="text-lg font-bold tracking-tight text-foreground">
                      {{ packageMeta()!.label }}
                    </div>
                    <p class="mt-0.5 text-xs leading-normal text-muted-foreground">
                      {{ packageMeta()!.description }}
                    </p>
                  </div>
                  <span
                    class="shrink-0 rounded-full bg-accent px-2.5 py-1 text-2xs font-bold text-accent-foreground"
                    >Automatisch ausgewählt</span
                  >
                </div>

                <div class="mt-4 grid grid-cols-2">
                  <div class="border-t border-border py-3">
                    <div class="text-xs text-muted-foreground">Adressbereich</div>
                    <div class="mt-0.5 font-mono text-sm text-foreground">
                      {{ packageMeta()!.addressRange }}
                    </div>
                  </div>
                  <div class="border-t border-border py-3">
                    <div class="text-xs text-muted-foreground">Preisstatus</div>
                    <div class="mt-0.5 font-mono text-sm text-foreground">
                      {{ packageMeta()!.priceRange }}
                    </div>
                  </div>
                </div>

                <p class="mt-3 max-w-[62ch] text-xs leading-normal text-muted-foreground text-pretty">
                  Das Paket wird automatisch aus der Anzahl Ihrer validierten Adressen bestimmt und
                  kann nicht manuell geändert werden.
                </p>
              </div>
              }
            </div>
          }

          <!-- ══════════════════════════════════════════════════════════════ -->
          <!-- STEP 5: Details                                               -->
          <!-- ══════════════════════════════════════════════════════════════ -->
          @if (currentStep() === 5) {

            <div class="flex flex-col gap-5">
              <!-- Name (required) -->
              <app-suffix-input
                label="Name"
                inputType="text"
                required
                [ngModel]="campaignTitle()"
                (ngModelChange)="campaignTitle.set($event)"
              />

              <div class="grid grid-cols-2 gap-5">
                <app-suffix-input
                  label="Standard-Jahresstromverbrauch"
                  suffix="kWh/Jahr"
                  [decimals]="0"
                  required
                  [ngModel]="defaultAnnualConsumptionKwh()"
                  (ngModelChange)="defaultAnnualConsumptionKwh.set($event)"
                  helpText="Wird für Adressen ohne eigenen Verbrauchswert verwendet."
                />
                <app-suffix-input
                  label="Standard-Strompreis"
                  suffix="€/kWh"
                  [decimals]="2"
                  required
                  [ngModel]="defaultElectricityPriceEurPerKwh()"
                  (ngModelChange)="defaultElectricityPriceEurPerKwh.set($event)"
                  helpText="Wird für Adressen ohne eigenen Strompreis verwendet."
                />
              </div>

              <!-- Description -->
              <app-textarea-field
                label="Beschreibung"
                [rows]="2"
                [ngModel]="campaignDescription()"
                (ngModelChange)="campaignDescription.set($event)"
              />

              <!-- Tags -->
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-normal text-foreground">Tags</label>
                <app-tag-input
                  id="c-tags"
                  [tags]="tags()"
                  (tagsChange)="tags.set($event)"
                  placeholder="Tag eingeben + Enter"
                  [allowAdd]="true"
                  [allowRemove]="true"
                  badgeLabel="Tag"
                />
              </div>

              <!-- Notes -->
              <app-textarea-field
                label="Interne Notizen"
                [rows]="2"
                [ngModel]="campaignNotes()"
                (ngModelChange)="campaignNotes.set($event)"
              />
            </div>
          }

          <!-- ══════════════════════════════════════════════════════════════ -->
          <!-- STEP 6: Summary / Confirmation                                -->
          <!-- ══════════════════════════════════════════════════════════════ -->
          @if (currentStep() === 6) {
            <div class="flex flex-col gap-6">
              <app-summary-section title="Kampagne" (edit)="goToStepNumber(5)">
                <app-summary-row label="Name" [value]="campaignTitle() || '—'" />
                <app-summary-row
                  label="Beschreibung"
                  [value]="campaignDescription() || 'Nicht angegeben'"
                  [tone]="campaignDescription() ? 'default' : 'muted'"
                />
                <app-summary-row
                  label="Tags"
                  [value]="tags().length ? tags().join(', ') : 'Keine'"
                  [tone]="tags().length ? 'default' : 'muted'"
                />
                <app-summary-row
                  label="Standardverbrauch"
                  [value]="((defaultAnnualConsumptionKwh() | number: '1.0-0') ?? '—') + ' kWh/Jahr'"
                  mono
                />
                <app-summary-row
                  label="Standard-Strompreis"
                  [value]="((defaultElectricityPriceEurPerKwh() | number: '1.2-4') ?? '—') + ' €/kWh'"
                  mono
                />
              </app-summary-section>

              <app-summary-section title="Adressen" (edit)="goToStepNumber(2)">
                <app-summary-row label="Quelle" [value]="sourceSummaryLine()" />
                <app-summary-row
                  label="Anzahl Adressen"
                  [value]="(effectiveAddresses() | number) ?? '0'"
                  mono
                />
                <app-summary-row label="Paket" [value]="packageMeta()?.label ?? '—'" />
                <app-summary-row label="Preisstatus" [value]="packageMeta()?.priceRange ?? '—'" />
                @if (totalFailedCount() > 0) {
                  <app-summary-row
                    label="Fehlgeschlagen"
                    [value]="'−' + ((totalFailedCount() | number) ?? '0')"
                    mono
                    tone="error"
                  />
                }
              </app-summary-section>

              <app-summary-section title="Nächste Schritte" [editable]="false">
                <app-summary-row label="1" value="Kampagne wird erstellt" />
                <app-summary-row label="2" value="Adressen werden hinzugefügt" />
                <app-summary-row label="3" value="Analysen stehen bereit" />
              </app-summary-section>

              <p class="max-w-[62ch] text-xs leading-normal text-muted-foreground text-pretty">
                Den Fortschritt sehen Sie in der Kampagnenübersicht.
              </p>
            </div>
          }

          <!-- Error Message -->
          @if (error()) {
            <div
              class="absolute bottom-0 left-0 right-0 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg"
            >
              <p class="text-sm">{{ error() }}</p>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Dialog Footer with Step Navigation -->
    <div hlmSeparator></div>
    <app-wizard-dialog-footer
      [currentStep]="currentStep()"
      [totalSteps]="totalSteps()"
      [isFirstStep]="isFirstStep()"
      [isLastStep]="isLastStep()"
      [canProceed]="canProceedFromCurrentStep()"
      [isSubmitting]="isSubmitting()"
      [submitLabel]="config().submitButtonText"
      (back)="prevStep()"
      (next)="nextStep()"
      (submit)="onSubmit(ctx)"
    />
  </hlm-dialog-content>
</hlm-dialog>
